import type { BookingStatus } from '@/types';

export interface StepInfo {
  step: number;
  title: string;
  subtitle: string;
  description: string;
}

/**
 * Mapeia status de booking para step do cliente (1-4)
 * Step 1: Finding filmmaker (paid, assigned)
 * Step 2: Filmmaker coming (en_route)
 * Step 2.5: Filmmaker arrived (arrived)
 * Step 3: Service in progress (in_progress)
 * Step 4: Service done (done_waiting_upload, uploaded, completed)
 */
export function getStepFromStatus(status: BookingStatus): number {
  switch (status) {
    case 'pending_payment':
    case 'paid':
    case 'assigned':
      return 1;
    case 'en_route':
      return 2;
    case 'arrived':
      return 2.5;
    case 'in_progress':
      return 3;
    case 'awaiting_finish_confirm':
    case 'done_waiting_upload':
    case 'uploaded':
    case 'completed':
      return 4;
    case 'canceled':
      return 0;
    default:
      return 1;
  }
}

/**
 * Retorna informações detalhadas sobre o step baseado no status
 */
export function getStepInfo(status: BookingStatus): StepInfo {
  const step = getStepFromStatus(status);
  
  switch (step) {
    case 1:
      return {
        step: 1,
        title: 'We are Finding Your Perfect Filmmaker',
        subtitle: 'Step 1 of 4',
        description: 'Matching you with the best available filmmakers in your area.'
      };
    
    case 2:
      return {
        step: 2,
        title: 'Your Filmmaker is Coming to You',
        subtitle: 'Step 2 of 4',
        description: 'Your filmmaker is on the way and should arrive shortly.'
      };
    
    case 2.5:
      return {
        step: 2.5,
        title: 'Your Filmmaker has Arrived',
        subtitle: 'Step 2 of 4',
        description: 'Please verify their identity by scanning the QR code badge they will present to you.'
      };
    
    case 3:
      return {
        step: 3,
        title: 'Service in Progress',
        subtitle: 'Step 3 of 4',
        description: 'Your session is in progress. Enjoy the experience!'
      };
    
    case 4:
      return {
        step: 4,
        title: 'Service Complete',
        subtitle: 'Step 4 of 4',
        description: 'Your session is complete. Thank you for your business!'
      };
    
    default:
      return {
        step: 0,
        title: 'Unknown Status',
        subtitle: '',
        description: ''
      };
  }
}

/**
 * Verifica se um status deve mostrar a página FindingFilmmaker
 */
export function shouldShowFindingFilmmaker(status: BookingStatus): boolean {
  return ['paid', 'assigned'].includes(status);
}

/**
 * Verifica se um status permite o cliente ver o filmmaker
 */
export function canCustomerSeeFilmmaker(status: BookingStatus): boolean {
  return ['en_route', 'arrived', 'in_progress', 'awaiting_finish_confirm', 
          'done_waiting_upload', 'uploaded', 'completed'].includes(status);
}

